import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { FuseConfirmDialogComponent } from "@fuse/components/confirm-dialog/confirm-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import { BootController } from "app/helpers/boot-controller";
import { BehaviorSubject, interval, Observable } from "rxjs";
import { map, takeWhile } from "rxjs/operators";
import { UserDataService } from "./user-data.service";

const STORE_KEY = "userLastAction";

@Injectable({
  providedIn: "root",
})
export class LogoutService implements OnDestroy {
  public static runTimer: boolean;
  public static runSecondTimer: boolean;
  public userIdlenessChecker: BehaviorSubject<string>;
  public secondLevelUserIdleChecker: BehaviorSubject<string>;
  private userActivityChangeCallback: ($event) => void;
  public USER_IDLE_TIMER_VALUE_IN_MIN: number = 15;
  public FINAL_LEVEL_TIMER_VALUE_IN_MIN: number = 1;
  private sessionForIdle: Observable<number>;
  public clockForIdle: Observable<number>;
  secondTimerLeft: string;
  idleTimerLeft: string;

  get lastAction(): number {
    return parseInt(localStorage.getItem(STORE_KEY), 10);
  }

  set lastAction(value) {
    localStorage.setItem(STORE_KEY, value.toString());
  }
  confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private ngZone: NgZone,
    private matDialog: MatDialog,
    private translateService: TranslateService
  ) {
    if (!this.userIdlenessChecker) {
      this.userIdlenessChecker = new BehaviorSubject<string>("INITIATE_TIMER");
    }

    if (!this.secondLevelUserIdleChecker) {
      this.secondLevelUserIdleChecker = new BehaviorSubject<string>(
        "INITIATE_SECOND_TIMER"
      );
    }
  }

  initTimer(): void {
    // Watcher on timer
    this.initilizeSessionTimeout();
    this.userIdlenessChecker.subscribe((status: string) => {
      this.initiateFirstTimer(status);
    });

    this.secondLevelUserIdleChecker.subscribe((status: string) => {
      this.initiateSecondTimer(status);
    });
  }

  initiateFirstTimer = (status: string) => {
    switch (status) {
      case "INITIATE_TIMER":
        break;

      case "RESET_TIMER":
        break;

      case "STOPPED_TIMER":
        this.confirmDialogRef = this.matDialog.open(
          FuseConfirmDialogComponent,
          {
            disableClose: false,
          }
        );
        this.confirmDialogRef.componentInstance.title = "User Inactive";

        this.translateService
          .get(["Time Remain", "Continue", "Logout"])
          .subscribe((tr_val) => {
            this.secondLevelUserIdleChecker.subscribe((val) => {
              this.confirmDialogRef.componentInstance.confirmMessage =
                tr_val["Time Remain"] + " : " + val;
            });
            this.confirmDialogRef.componentInstance.positive_btn =
              tr_val.Continue;
            this.confirmDialogRef.componentInstance.negative_btn =
              tr_val.Logout;
          });

        this.confirmDialogRef.componentInstance.title = "";
        this.confirmDialogRef.afterClosed().subscribe((result) => {
          if (result) {
            LogoutService.runSecondTimer = false;
            this.initilizeSessionTimeout();
          } else {
            this.logout();
          }
        });
        break;

      default:
        // this.idleTimerLeft = this.formatTimeLeft(Number(status));
        break;
    }
  };

  initiateSecondTimer = (status: string) => {
    switch (status) {
      case "INITIATE_SECOND_TIMER":
        break;

      case "SECOND_TIMER_STARTED":
        break;

      case "SECOND_TIMER_STOPPED":
        this.logout();
        if (this.confirmDialogRef) {
          this.confirmDialogRef.close();
        }
        break;

      default:
        this.secondTimerLeft = status;
        break;
    }
  };

  public initilizeSessionTimeout(): void {
    LogoutService.runTimer = true;
    if (this.USER_IDLE_TIMER_VALUE_IN_MIN === 0) {
      this.userIdlenessChecker.thrownError(
        "Please provide USER_IDLE_TIMER_VALUE in minuite"
      );
      return;
    }

    this.reset();
    this.initListener();
    this.initInterval();
  }

  private initInterval(): void {
    const intervalDuration = 1000;
    this.sessionForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick;
      }),
      takeWhile(() => LogoutService.runTimer)
    );

    this.check();
  }

  private check(): void {
    this.sessionForIdle.subscribe(() => {
      const now = Date.now();
      const timeleft =
        this.lastAction + this.USER_IDLE_TIMER_VALUE_IN_MIN * 60 * 1000;
      const diff = timeleft - now;
      const isTimeout = diff < 0;

      this.userIdlenessChecker.next(`${diff}`);

      if (isTimeout) {
        window.document.removeEventListener(
          "click",
          this.userActivityChangeCallback,
          true
        );
        this.ngZone.run(() => {
          if (this.userIdlenessChecker) {
            this.userIdlenessChecker.next("STOPPED_TIMER");

            if (this.FINAL_LEVEL_TIMER_VALUE_IN_MIN > 0) {
              this.secondLevelUserIdleChecker.next("SECOND_TIMER_STARTED");
              this.executeFinalTimer();
            }
          }
          LogoutService.runTimer = false;
        });
      }
    });
  }

  private executeFinalTimer = () => {
    LogoutService.runSecondTimer = true;
    this.initializeFinalTimer();
  };

  private initializeFinalTimer(): void {
    const intervalDuration = 1000;
    this.clockForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick;
      }),
      takeWhile(() => LogoutService.runSecondTimer)
    );

    this.checkUserActionTime();
  }

  private checkUserActionTime(): void {
    let timeInSecond = 60;
    let timeInMin = this.FINAL_LEVEL_TIMER_VALUE_IN_MIN - 1;
    this.clockForIdle.subscribe(() => {
      if (--timeInSecond === 0) {
        if (--timeInMin === 0) {
          timeInMin = timeInMin > 0 ? timeInMin - 1 : 0;
        }
        if (timeInMin === -1 && timeInSecond === 0) {
          LogoutService.runSecondTimer = false;

          if (this.secondLevelUserIdleChecker) {
            this.secondLevelUserIdleChecker.next("SECOND_TIMER_STOPPED");
          }
        }
        if (timeInMin < 0) {
          timeInMin = 0;
          setTimeout(() => {
            timeInSecond = 60;
          }, 800);
        } else {
          timeInSecond = 60;
        }
      }

      this.secondLevelUserIdleChecker.next(`${timeInMin}:${timeInSecond}`);
    });
  }

  logout = () => {
    this.clear();
    this.ngZone.runOutsideAngular(() =>
      BootController.getbootControl().restart()
    );
    // this.router.navigate([""]);
    location.href = "";
  };

  clear = () => {
    localStorage.clear();
  };

  private initListener(): void {
    this.ngZone.runOutsideAngular(() => {
      this.userActivityChangeCallback = ($event) =>
        this.handleUserActiveState($event);

      window.document.addEventListener(
        "click",
        this.userActivityChangeCallback.bind(this),
        true
      );
    });
  }

  handleUserActiveState(event): void {
    this.reset();
  }

  public reset(): void {
    this.lastAction = Date.now();
    if (this.userIdlenessChecker) {
      this.userIdlenessChecker.next("RESET_TIMER");
    }
  }

  ngOnDestroy(): void {
    if (this.userIdlenessChecker) {
      this.userIdlenessChecker.unsubscribe();
      this.userIdlenessChecker = undefined;
    }

    if (this.secondLevelUserIdleChecker) {
      this.secondLevelUserIdleChecker.unsubscribe();
      this.secondLevelUserIdleChecker = undefined;
    }
  }
}
